import { IconButton, Typography } from "@material-tailwind/react";
import React from "react";
import { PlayIcon, PauseIcon } from "@heroicons/react/24/solid";
import {
  MusicalNoteIcon,
  EyeIcon
} from "@heroicons/react/24/outline";
import { useNavigate } from "react-router-dom";
import SharePopover from "./SharePopover";
import { getYear, returnSubCategoryName, toFixed } from "../helper/helper";
import FavouriteBtn from "./FavouriteBtn";
import { DetailPageUrl } from "../services/API";
import { getItemAccessType, isUserSubscribed } from "../helper/accessTypeHelper";
import { useReduxStore } from "../hooks/useReduxStore";

export default function DetailBanner3({
  rating = "4.0",
  views = 1,
  type = "movie",
  description,
  image,
  title,
  author,
  artist,
  sub_category = [],
  released_date,
  publish_date,
  duration_time = "",
  onClickBuy,
  isPurchased = false,
  onWishClick,
  onPlayClick,
  price,
  exclusive_content,
  ppv_content,
  is_favourite,
  isPlayerOpen,
  id,
  onFeaturedClick,
  feature_video,
  dark = false
}) {
  const { getState } = useReduxStore();
  const { userData } = getState("AUTH");
  sub_category = returnSubCategoryName(sub_category);

  const isMusic = type === "Audio" || type === "music";
  const typeName = "Music";
  const navigate = useNavigate();

  const access = getItemAccessType({
    price,
    exclusive_content,
    ppv_content,
  });

  const isSubscribed = isUserSubscribed(userData);

  const isEffectivelyAccessible =
    isPurchased || access.isFree || (access.isExclusive && isSubscribed);

  const onPlay = () => {
    onPlayClick();
  };

  const onFirstBtnPress = () => {
    if (isEffectivelyAccessible) {
      onPlay();
    } else if (access.isExclusive) {
      navigate("/Subscription");
    } else {
      onClickBuy();
    }
  };

  return (
    <div className="px-4 sm:px-6 lg:px-14 py-8">
      <section
        id="Detail"
        className={`relative overflow-hidden rounded-2xl border ${dark
          ? "bg-zinc-900/40 border-zinc-800/80 backdrop-blur-md shadow-2xl shadow-black/40"
          : "bg-white border-gray-150/80 shadow-xl"
          } p-6 sm:p-8 lg:p-12`}
      >
        <div className="flex flex-col-reverse lg:flex-row gap-8 lg:gap-16 items-center justify-between">
          {/* Metadata details */}
          <div className="flex flex-col justify-center flex-1 w-full text-center lg:text-left">
            <div className="flex items-center justify-center lg:justify-start gap-2 mb-2">
              <span className="text-secondary text-xs sm:text-sm font-bold uppercase tracking-widest block">
                {typeName}
              </span>
              <span
                className={`px-2.5 py-0.5 rounded-full text-[10px] font-extrabold uppercase tracking-wide ${access.badgeClass}`}
              >
                {access.badgeText}
              </span>
            </div>

            <h2 className={`text-3xl lg:text-5xl font-bold tracking-tight mb-4 ${dark ? "text-white" : "text-black"}`}>
              {title || "Track Title"}
            </h2>

            <div className="flex items-center justify-center lg:justify-start gap-4 mb-4 text-xs sm:text-sm font-medium">
              <span className="flex items-center gap-1.5 text-secondary">
                <MusicalNoteIcon className="h-4 w-4" />
                {sub_category || "General"}
              </span>
              <span className={`h-1 w-1 rounded-full ${dark ? "bg-zinc-700" : "bg-gray-300"}`}></span>
              <span className="flex items-center gap-1.5 text-zinc-400">
                <EyeIcon className="h-4 w-4" />
                {views || 0} views
              </span>
              {(publish_date || released_date) && (
                <>
                  <span className={`h-1 w-1 rounded-full ${dark ? "bg-zinc-700" : "bg-gray-300"}`}></span>
                  <span className="text-zinc-400">
                    {getYear(publish_date || released_date)}
                  </span>
                </>
              )}
            </div>

            {description && (
              <p className={`mb-6 text-sm sm:text-base leading-relaxed ${dark ? "text-zinc-400" : "text-gray-600"} line-clamp-3`}>
                {description}
              </p>
            )}

            <Typography className={`text-xs sm:text-sm mb-4 font-semibold ${dark ? "text-zinc-300" : "text-gray-800"}`}>
              Artist : <span className="text-secondary font-bold">{artist || author || "Unknown Artist"}</span>
            </Typography>

            {!isPurchased && (
              <div className="mb-6 flex items-center justify-center lg:justify-start gap-2">
                <Typography className={`text-sm sm:text-base font-semibold ${dark ? "text-zinc-300" : "text-blue-gray-900"}`}>
                  {access.isFree
                    ? "Access : "
                    : access.isExclusive
                    ? "Membership : "
                    : access.isPpv
                    ? "Rental Price : "
                    : "Price : "}
                </Typography>
                {access.isFree ? (
                  <span className="text-emerald-400 text-lg font-black bg-emerald-500/10 px-2.5 py-0.5 rounded border border-emerald-500/30">
                    FREE
                  </span>
                ) : access.isExclusive ? (
                  isSubscribed ? (
                    <span className="text-secondary text-sm font-bold bg-secondary/15 px-3 py-1 rounded-full border border-secondary/30 flex items-center gap-1.5">
                      <span>👑</span> Included with VIP Membership
                    </span>
                  ) : (
                    <span className="text-amber-400 text-base font-black bg-amber-500/10 px-2.5 py-0.5 rounded border border-amber-500/30">
                      Subscribers Only
                    </span>
                  )
                ) : access.isPpv ? (
                  <span className="text-purple-300 text-lg font-black bg-purple-500/15 px-2.5 py-0.5 rounded border border-purple-500/30">
                    $ {toFixed(price)} (PPV)
                  </span>
                ) : (
                  <span className="text-secondary text-lg font-bold">
                    $ {toFixed(price)}
                  </span>
                )}
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start items-center">
              <button
                onClick={onFirstBtnPress}
                className="w-full sm:w-auto px-8 py-3 rounded-lg font-bold text-sm bg-secondary hover:bg-secondary/90 text-white shadow-lg shadow-secondary/20 transition-all duration-300 hover:scale-[1.02] active:scale-98 flex items-center justify-center gap-2"
              >
                {isPlayerOpen ? (
                  <PauseIcon className="h-4 w-4 fill-white text-white" />
                ) : (
                  <PlayIcon className="h-4 w-4 fill-white text-white" />
                )}
                {!isEffectivelyAccessible
                  ? access.isExclusive
                    ? "Subscribe to Play"
                    : access.isPpv
                    ? "Rent PPV & Play"
                    : "Buy and "
                  : ""}
                {isPlayerOpen ? "Pause" : "Play Now"}
              </button>

              {feature_video && (
                <button
                  onClick={onFeaturedClick}
                  className="w-full sm:w-auto px-6 py-3 rounded-lg font-bold text-sm border border-zinc-700 hover:border-secondary bg-white/5 hover:bg-white/10 text-white transition-all duration-300 hover:scale-[1.02] active:scale-98 flex items-center justify-center gap-2"
                >
                  Watch Promo
                </button>
              )}

              <div className="flex items-center gap-4 mt-2 sm:mt-0">
                <FavouriteBtn is_favourite={is_favourite} onClick={onWishClick} dark={dark} />
                <SharePopover link={`${DetailPageUrl}?id=${id}`} dark={dark} />
              </div>
            </div>
          </div>

          {/* Interactive CD/Vinyl Disk Mockup Cover */}
          <div className="relative flex items-center justify-center w-56 h-56 sm:w-72 sm:h-72 lg:w-80 lg:h-80 mr-4 sm:mr-8">
            {/* Spinning Vinyl Disc behind the album art */}
            <div className={`absolute w-[88%] h-[88%] rounded-full vinyl-disc animate-spin-slow shadow-xl shadow-black/70 flex items-center justify-center transition-transform duration-500 ${isPlayerOpen ? "translate-x-14" : "translate-x-0"}`}>
              <div className="w-[32%] h-[32%] rounded-full bg-black border border-zinc-800 flex items-center justify-center">
                <div className="w-5 h-5 rounded-full bg-zinc-900 border border-zinc-800"></div>
              </div>
            </div>

            {/* Real Album Cover Art */}
            <div className="relative z-10 w-[82%] h-[82%] rounded-2xl overflow-hidden shadow-2xl shadow-black/80 border border-zinc-700/30 transform hover:scale-[1.02] transition-transform duration-500 bg-black">
              <img
                className="w-full h-full object-cover"
                src={
                  image ||
                  "https://ntvb.tmsimg.com/assets/p7825626_v_h10_aa.jpg?w=1280&h=720"
                }
                alt={title}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent pointer-events-none" />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

